import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { useModal } from "react-modal-hook";

import { useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { TemplateClassNames, EditorSizes } from "../../../../../../types/template-layouts";
import { PageModule, Session } from "../../../../../../types/working-model";
import { EIcon } from "../../../../../general-ui/icon/icon";
import ModalComponent from "../../../../../general-ui/modal/modal";
import Search from "../../../../../general-ui/search-bar/search";
import { SelectOption } from "../../../../../general-ui/select/select";
import TranslateLangs from '../../../../../../utils/translate-langs.json';
import { ILang } from "../../../../../general-ui/language-dropdown/language-dropdown";
import FilterDropdown from "../../../../../live-event/modules/agenda/session-filter-dropdown";
import '../../../../../../scss/live-event/base/modules/session-filter.scss';
import { useTranslate } from "../../../../../../i18n/useTranslationModules";
import { useParams } from 'react-router';
import { ParamsProps } from "../../../../../live-event/live-event";
import { formatMonthAndDay } from "../../../../../../utils/date-utils";

const ALL_LANGS: ILang[] = TranslateLangs as any;

export interface SessionFilterArgs {
	language?: string;
	search?: string;
	category?: string;
	day?: number;
}
interface SessionFilterProps {
	module: PageModule;
	template: string | undefined;
	sessions: Session[];
	isLive: boolean;
	onFilterChange?: (props: SessionFilterArgs) => void;
	overrides: string;
}
export const SessionFilter = ({
	sessions,
	onFilterChange,
	isLive,
	template,
	overrides = ''
}: SessionFilterProps): JSX.Element => {
	const { language } = useParams() as ParamsProps;
	const {
		editorSize
	} = useTypedSelector(event => event.CreateEventReducer);

	const { t } = useTranslate("homepage");

	const [selectedLanguage, setSelectedLanguage] = useState<string>("");
	const [selectedDate, setSelectedDate] = useState<number>(0);
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [, setSearch] = useState<string>();

	const isLessThan768 = editorSize === EditorSizes.mobile;

	const daysOptions = useMemo<SelectOption[]>(() => {
		const existingDays: (string | number)[] = [];
		const result = sessions
			.map((s) => {
				const date = new Date(s.timestamp || "");
				if (isNaN(date.getTime())) {
					return null;
				}
				
				const label = formatMonthAndDay({ date, locale: language });
				
				return {
					label,
					value: s.timestamp || 0,
				} as SelectOption;
			})
			.filter((f) => {
				if (!f) return false;
				// dont include duplicate days
				// use labels as timestamps are different for same day events with differing times
				const alreadyExists = existingDays.includes(f.label);
				existingDays.push(f.label);
				return !alreadyExists;
			}) as SelectOption[];
		return result;
	}, [language, sessions]);
	
	const inlineDaysOptions = useMemo<SelectOption[]>(() => {
		return [{ label: t("All"), value: 0 }, ...daysOptions];
	}, [daysOptions, t]);
	
	const selectDaysOptions = useMemo<SelectOption[]>(() => {
		return [{ label: t("All dates"), value: 0 }, ...daysOptions];
	}, [daysOptions, t]);

	const languageOptions = useMemo<SelectOption[]>(() => {
		const result = sessions
			.map((s) => s.languages)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map((s) => {
				const option = {
					label: ALL_LANGS.find(f => f.code === s)?.label,
					value: s
				};
				return option;
			});
		return [{ label: "All languages", value: "" }, ...result];
	}, [sessions]);

	const categoryOptions = useMemo<SelectOption[]>(() => {
		const result = sessions
			.map((s) => s.tracks)
			.flat()
			.filter((x, i, a) => a.indexOf(x) === i)
			.map(
				(s) =>
				({
					label: s,
					value: s,
				} as SelectOption)
			)
			.sort((a: SelectOption, b: SelectOption) => a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1);

		return [{ label: "All tracks", value: "" }, ...result];
	}, [sessions]);

	const renderInlineDays = () => {
		const data = inlineDaysOptions.map((day) => {
			const label = day.label;

			return (
				<button
					key={day.value}
					onClick={() => {
						setSelectedDate(day.value as number);
						onFilterChange &&
							onFilterChange({
								category: selectedCategory,
								day: day.value as number,
								language: selectedLanguage,
							});
					}}
					className={classNames('evt-button secondary session-day-button', {
						"session-selected-day ": day.value === selectedDate,
					})}
				>
					{label}
				</button>
			);
		});

		return (
			<div className="agenda-inline-days">
				{data}
			</div>
		);
	};

	const renderDaysFilter = (): JSX.Element => {
		return (
			<>
				{/* Test to any value for test inline view */}
				{/* Tablet view to always render dropdown */}
				{daysOptions.length < 4 && (editorSize === EditorSizes.desktop) ? (
					renderInlineDays()
				) : (
					<FilterDropdown
						options={selectDaysOptions}
						onChange={(option) => {
							const value = option.value.toString();
							const date = parseInt(value, 10);
							setSelectedDate(date);

							onFilterChange &&
								onFilterChange({
									category: selectedCategory,
									day: date,
									language: selectedLanguage,
								});
						}}
						template={template || TemplateClassNames.Apollo}
						value={selectedDate}
					/>
				)}
			</>
		);
	};

	const handleSearchChange = useCallback(
		(text) => {
			setSearch(text);
			onFilterChange &&
				onFilterChange({
					category: selectedCategory,
					search: text,
					language: selectedLanguage,
					day: selectedDate,
				});
		},
		[onFilterChange, selectedCategory, selectedDate, selectedLanguage]
	);

	const [showFilterModal, hideFilterModal] = useModal(() => {
		return (
			<ModalComponent
				className={classNames("evt-modal-filters live-event-container", template)}
				title={``}
				onRequestClose={hideFilterModal}
				open={true}
				closeable
				cancellable
			>
				{renderContent()}
			</ModalComponent>
		);
	}, [isLessThan768, selectedCategory, selectedDate, selectedLanguage, template]);

	const renderSearch = (): JSX.Element => (
		<Search
			onSearch={handleSearchChange}
			onClear={() => handleSearchChange("")}
		/>
	);

	const renderContent = (): JSX.Element => {
		return (
			<>
				{daysOptions.length > 1 ? renderDaysFilter() : null}
				<FilterDropdown
					options={languageOptions}
					onChange={(option) => {
						const value = option.value.toString();
						setSelectedLanguage(value);

						onFilterChange &&
							onFilterChange({
								category: selectedCategory,
								day: selectedDate,
								language: value,
							});
					}}
					template={template || TemplateClassNames.Apollo}
					value={selectedLanguage}
					isLangDropdown
				/>
				<FilterDropdown
					options={categoryOptions}
					onChange={(option) => {
						const value = option.value.toString();
						setSelectedCategory(value);

						onFilterChange &&
							onFilterChange({
								category: value,
								day: selectedDate,
								language: selectedLanguage,
							});
					}}
					template={template || TemplateClassNames.Apollo}
					value={selectedCategory}
				/>
			</>
		);
	};

	return (
		<div className={
			classNames('agenda-filters', overrides)
		}>
			{renderSearch()}
			{renderContent()}
		</div>
	);
};

export const filterSessions = ({
	sessions,
	filter,
}: {
	sessions: Session[];
	filter?: SessionFilterArgs;
}): Session[] => {
	let result = sessions;
	if (filter?.category) {
		result = result.filter((f) => f.tracks?.includes(filter.category || ""));
	}
	if (filter?.language) {
		result = result.filter((f) => f.languages.includes(filter.language || ""));
	}
	if (filter?.day) {
		//turn timestamps into days, to get rid of the exact time factor in timestamps
		const getDay = (date: Date) => date.toLocaleString("default", { month: "long" }) + date.getDate();
		result = result.filter((f) => {
			if (!f.timestamp) return false;
			const currentDay = getDay(new Date(f.timestamp));
			const filterDay = getDay(new Date(filter.day!));
			return currentDay === filterDay;
		});
	}
	return result;
};